import { Grid } from '@achieve/ascend'
import { useCallback, useMemo } from 'react'
import { Typography } from 'components/Typography'
import { useTheme } from '@emotion/react'
import Gauge from 'components/Svgs/Gauge'
import GaugeArrow from 'components/Svgs/GaugeArrow'

export const DtiRadialMeter = ({
  leftTitle = '35%',
  leftLabel = 'or less is good',
  middleTitle = '36% to 43%',
  middleLabel = 'needs work',
  rightTitle = '44%',
  rightLabel = 'or more is high',
  dtiRatio = 0,
}) => {
  const theme = useTheme()
  const titleMap = useMemo(
    () => ({
      default: {
        title: `Your estimated DTI ratio`,
        fontColor: theme.ascend.colors.content.state.accentHover,
        backgroundColor: theme.ascend.colors.blue[25],
      },
      low: {
        title: `Your ${dtiRatio}% DTI is healthy`,
        fontColor: theme.ascend.colors.content.positive,
        backgroundColor: theme.ascend.colors.green[50],
      },
      mid: {
        title: `Your ${dtiRatio}% DTI could improve`,
        fontColor: theme.ascend.colors.yellow[400],
        backgroundColor: theme.ascend.colors.yellow[50],
      },
      high: {
        title: `Your ${dtiRatio}% DTI needs action`,
        fontColor: theme.ascend.colors.red[400],
        backgroundColor: theme.ascend.colors.red[50],
      },
    }),
    [dtiRatio, theme]
  )

  if (dtiRatio > 100) {
    dtiRatio = 100
  }

  const rotation = (dtiRatio * 180) / 100

  const gaugeArrowStyle = useMemo(
    () => ({
      top: '85%',
      left: '25%',
      transform: `rotate(${rotation}deg)`,
    }),
    [rotation]
  )

  const rotationToTextSelection = useCallback(
    (rotation) => {
      if (rotation === 0) {
        return 'default'
      }
      if (middleTitle.indexOf('-')) {
        const min = parseInt(middleTitle.split('to')[0].replace(/\D/g, ''))
        const max = parseInt(middleTitle.split('to')[1].replace(/\D/g, ''))
        if (min <= rotation && max >= rotation) {
          return 'mid'
        }
      }

      if (rotation <= parseInt(leftTitle.replace(/\D/g, ''))) {
        return 'low'
      }
      if (rotation >= parseInt(rightTitle.replace(/\D/g, ''))) {
        return 'high'
      }
      return 'default'
    },
    [middleTitle, leftTitle, rightTitle]
  )

  const title = rotationToTextSelection(dtiRatio)

  const eyebrowStyle = useMemo(
    () => ({
      color: titleMap[title].fontColor,
      backgroundColor: titleMap[title].backgroundColor,
    }),
    [titleMap, title]
  )

  const focusedTextColor = theme.ascend.colors.content.primary
  const unfocusedTextColor = theme.ascend.colors.content.state.disabled
  return (
    <>
      <div
        className="flex flex-col flex-nowrap self-center justify-evenly p-2 rounded min-w-[320px] min-h-[200px]"
        data-testid="dti-radial-meter-section"
      >
        <div className="flex flex-nowrap justify-center">
          <Typography
            className={`my-5 px-4 py-2 rounded`}
            variant="labelMd"
            fontWeight="bold"
            data-testid="dti-radial-meter-eyebrow"
            style={eyebrowStyle}
          >
            {titleMap[title].title}
          </Typography>
        </div>
        <div className="flex flex-col justify-center" data-testid="dti-radial-meter-container">
          <div className="flex flex-col" data-testid="dti-radial-middle-text-container">
            <Typography
              className={`text-center text-[${
                title === 'mid' ? focusedTextColor : unfocusedTextColor
              }]`}
              variant="displayMd"
              fontWeight="medium"
              data-testid="dti-radial-meter-middle-title"
            >
              {middleTitle}
            </Typography>
            <Typography
              variant="bodyXs"
              className={`text-center text-[${
                title === 'mid' ? focusedTextColor : unfocusedTextColor
              }]`}
              data-testid="dti-radial-meter-middle-label"
            >
              {middleLabel}
            </Typography>
          </div>

          <div className="flex flex-nowrap justify-center">
            <div
              className="flex flex-col max-w-[70px] text-right justify-end"
              data-testid="dti-radial-left-text-container"
            >
              <Typography
                variant="displayMd"
                fontWeight="medium"
                className={`text-right text-[${
                  title === 'low' ? focusedTextColor : unfocusedTextColor
                }]`}
                data-testid="dti-radial-meter-left-title"
              >
                {leftTitle}
              </Typography>
              <Typography
                variant="bodyXs"
                className={`text-right text-[${
                  title === 'low' ? focusedTextColor : unfocusedTextColor
                }]`}
                data-testid="dti-radial-meter-left-label"
              >
                {leftLabel}
              </Typography>
            </div>
            <Grid item container className="h-[100px] w-[200px]" data-testid="dti-gauge-container">
              <div className="relative flex w-[200px]">
                <Gauge className="absolute left-0 right-0 mx-auto z-10" data-testid="dti-gauge" />
                <GaugeArrow
                  className="absolute z-10 transition-transform duration-800"
                  style={gaugeArrowStyle}
                  data-testid="dti-gauge-arrow"
                />
              </div>
            </Grid>
            <div
              className="flex flex-col justify-end max-w-[70px]"
              data-testid="dti-radial-right-text-container"
            >
              <Typography
                variant="displayMd"
                fontWeight="medium"
                className={`text-left text-[${
                  title === 'high' ? focusedTextColor : unfocusedTextColor
                }]`}
                data-testid="dti-radial-meter-right-title"
              >
                {rightTitle}
              </Typography>
              <Typography
                variant="bodyXs"
                className={`text-left text-[${
                  title === 'high' ? focusedTextColor : unfocusedTextColor
                }]`}
                data-testid="dti-radial-meter-right-label"
              >
                {rightLabel}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
